import React from "react";
import { Box, Text, Flex, Tooltip } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

/**
 * Enhanced AgendaButton component with Figma designs and internal state management
 * Handles both legacy props and new enhanced API
 */
const AgendaButton = ({
  // Legacy props (for backward compatibility)
  variant,
  disabled = false,
  onClick,
  isLoading = false,
  tooltip,
  children,

  // New enhanced props
  agendaState = "stopped", // 'live' | 'stopped' | 'starting' | 'stopping'

  // Test and accessibility
  "data-testid": dataTestId,
  id,

  // Any other props
  ...rest
}) => {
  // Map external state to internal UI state
  const getUIState = () => {
    // If explicitly disabled, show disabled state
    if (disabled) {
      return "disabled";
    }

    // If loading, show loading state
    if (isLoading) {
      return "loading";
    }

    // Map agenda state to UI state
    switch (agendaState) {
      case "live":
      case "stopping":
        return "end";
      case "stopped":
      case "starting":
      default:
        return "start";
    }
  };

  // Design tokens based on Figma
  const getButtonStyles = () => {
    const uiState = getUIState();

    switch (uiState) {
      case "end":
        return {
          bg: "#CC474E", // Red from Figma
          color: "#FFFFFF",
          text: "STOP",
          hoverBg: "#B83D44",
          activeBg: "#A53339",
        };
      case "loading":
        return {
          bg: "#9CA3AF", // Gray for loading
          color: "#FFFFFF",
          text: isLoading ? "Loading..." : "Start Agenda",
          hoverBg: "#9CA3AF",
          activeBg: "#9CA3AF",
        };
      case "disabled":
        return {
          bg: "#E5E7EB", // Light gray for disabled
          color: "#9CA3AF",
          text: "Start Agenda",
          hoverBg: "#E5E7EB",
          activeBg: "#E5E7EB",
        };
      case "start":
      default:
        return {
          bg: "#389D1B", // Green from Figma
          color: "#FFFFFF",
          text: "Start Agenda",
          hoverBg: "#2F8616",
          activeBg: "#266F12",
        };
    }
  };

  // Enhanced tooltip logic
  const getTooltip = () => {
    if (tooltip) {
      return tooltip;
    }

    // Show "Agenda Complete" for past/ended agendas
    if (disabled && agendaState === "stopped" && rest.isPastAgenda) {
      return "Agenda Complete";
    }

    // Auto-generate helpful tooltips based on state
    if (disabled && agendaState === "stopped") {
      return "Please complete setup to start the agenda";
    }

    const isLoadingState =
      isLoading || agendaState === "starting" || agendaState === "stopping";
    if (isLoadingState) {
      return agendaState === "starting"
        ? "Starting agenda..."
        : "Stopping agenda...";
    }

    return null;
  };

  const buttonStyles = getButtonStyles();
  const isLoadingState =
    isLoading || agendaState === "starting" || agendaState === "stopping";
  const isInteractive = !disabled && !isLoadingState;

  const buttonElement = (
    <Box
      as="button"
      id={id}
      data-testid={dataTestId}
      onClick={isInteractive ? onClick : undefined}
      cursor={isInteractive ? "pointer" : "not-allowed"}
      borderRadius="8px"
      bg={buttonStyles.bg}
      color={buttonStyles.color}
      padding="8px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minWidth="fit-content"
      transition="all 0.2s ease"
      opacity={disabled ? 0.6 : 1}
      _hover={
        isInteractive
          ? {
              bg: buttonStyles.hoverBg,
              transform: "translateY(-1px)",
              boxShadow: "md",
            }
          : {}
      }
      _active={
        isInteractive
          ? {
              bg: buttonStyles.activeBg,
              transform: "translateY(0)",
              boxShadow: "sm",
            }
          : {}
      }
      _focus={{
        outline: "none",
        boxShadow: "0 0 0 2px rgba(56, 157, 27, 0.3)",
      }}
      {...rest}
    >
      <Flex direction="row" align="center" gap="6px">
        {/* Button Text */}
        <Text
          fontSize="xs"
          fontWeight="500"
          lineHeight="1.23"
          letterSpacing="0.02em"
          fontFamily="Geist, system-ui, sans-serif"
        >
          {buttonStyles.text}
        </Text>

        {/* Loading spinner */}
        {isLoadingState && (
          <Box
            as="div"
            width="11px"
            height="11px"
            border="1px solid transparent"
            borderTop="1px solid currentColor"
            borderRadius="50%"
            animation="spin 1s linear infinite"
            sx={{
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
      </Flex>
    </Box>
  );

  // Wrap with tooltip if provided
  const finalTooltip = getTooltip();
  if (finalTooltip) {
    return (
      <Tooltip
        hasArrow
        shouldWrapChildren
        label={finalTooltip}
        placement="top"
        bg="gray.700"
        color="white"
        fontSize="sm"
        borderRadius="md"
        px={3}
        py={2}
      >
        {buttonElement}
      </Tooltip>
    );
  }

  return buttonElement;
};

export default AgendaButton;
